import styled, { css } from 'styled-components';
import { AllowableGutter, IGapSizeProps, IGutterAsMarginProps, SPACE_DEFAULT, useGapCss, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import React, { Children, useEffect, useState } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { TabsContext } from './TabsContext';
import { ITabsProps, TabsSize, UNDERLINE_HIGHLIGHT_SIZE } from './Tabs';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { IconButton } from '../IconButton/IconButton';
import { MATERIAL_ICONS } from '../Icon/icons';
import { HorizontalScrollContainer } from '../HorizontalScroll/HorizontalScrollContainer';
import { useIsScrolling } from '../../hooks/useIsScrolling/useIsScrolling';
import { BaseDiv } from '../../common/BaseElements';

export interface ITabListProps extends React.LiHTMLAttributes<HTMLDivElement>, IGutterAsMarginProps {
  isHorizontalScrolling?: boolean;
  autoHorizontalScrolling?: boolean;
}

const tabGaps: Record<TabsSize, AllowableGutter> = {
  [SIZES.X_SMALL]: 8,
  [SIZES.SMALL]: 12,
  [SIZES.MEDIUM]: 16
};

const TabListContainer = styled(BaseDiv)<ITabListProps & IGapSizeProps & Pick<ITabsProps, 'underline' | 'size'> & { shouldScroll: boolean }>`
  ${({ size }) => useGapCss(tabGaps[size!])}
  ${({ isHorizontalScrolling, autoHorizontalScrolling, shouldScroll }) =>
    (isHorizontalScrolling || (autoHorizontalScrolling && shouldScroll)) &&
    /* istanbul ignore next */ css`
      display: block;
    `}
  ${useGutterAsMarginCss({ bottom: SPACE_DEFAULT })}
  ${({ underline, theme }) =>
    underline === 'bordered' &&
    css`
      border-bottom: solid ${UNDERLINE_HIGHLIGHT_SIZE} ${theme.colours.palette.neutral50};
    `};
  ${({ autoHorizontalScrolling, shouldScroll }) =>
    autoHorizontalScrolling &&
    !shouldScroll &&
    css`
      overflow-x: hidden;
    `}
`;

const AddTabButtonContainer = styled(BaseDiv)``;

const ARROW_SIZE = 28;

export const determineShouldScroll = (
  shouldScroll: boolean,
  containerRef: HTMLDivElement | null,
  setShouldScroll: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!containerRef) return;
  const scrollContainerRef = containerRef.querySelector('.react-horizontal-scrolling-menu--scroll-container');
  const innerWrapperRef = containerRef.querySelector('.react-horizontal-scrolling-menu--inner-wrapper');
  if (shouldScroll && scrollContainerRef && innerWrapperRef) {
    setShouldScroll(scrollContainerRef.scrollWidth + 2 * ARROW_SIZE > innerWrapperRef.clientWidth);
  }
};

export const TabList = React.forwardRef<HTMLDivElement, ITabListProps>(({ isHorizontalScrolling, autoHorizontalScrolling = false, children, ...rest }, ref) => {
  const { generateTestId } = useTestId(rest, 'TabList');
  const { underline, size, onAddTabClick, active } = React.useContext(TabsContext);
  const [containerRef, { isHorizontalScrolling: isContainerHorizontalScrolling }] = useIsScrolling();
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (!autoHorizontalScrolling) return;
    if (!containerRef.current) return;

    /* istanbul ignore else */
    if (isContainerHorizontalScrolling) {
      /* istanbul ignore next */
      setShouldScroll(true);
    }

    determineShouldScroll(shouldScroll, containerRef.current, setShouldScroll);
    const resizeObserver = new ResizeObserver(
      /* istanbul ignore next */ () => {
        /* istanbul ignore next */
        determineShouldScroll(shouldScroll, containerRef.current, setShouldScroll);
      }
    );
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerRef.current, autoHorizontalScrolling, isContainerHorizontalScrolling]);

  return (
    <TabListContainer
      {...generateTestId()}
      underline={underline}
      size={size}
      isHorizontalScrolling={isHorizontalScrolling}
      autoHorizontalScrolling={autoHorizontalScrolling}
      shouldScroll={shouldScroll}
      {...rest}
      ref={ref ?? containerRef}
    >
      <HorizontalScrollContainer
        isApplied={isHorizontalScrolling ?? (autoHorizontalScrolling && shouldScroll)}
        scrollToItem={active ? active - 1 : undefined}
        gapContainer={4}
        gapInner={4}
        showAddTabButton={!!onAddTabClick}
      >
        {onAddTabClick && (
          <AddTabButtonContainer>
            <IconButton {...generateTestId('AddTab')} size={size} icon={MATERIAL_ICONS.PlaylistAdd} onClick={onAddTabClick} />
          </AddTabButtonContainer>
        )}
        {Children.map(children as React.ReactElement<{ position?: number }>[], (tab, position) => React.cloneElement(tab, { position: position + 1 }))}
      </HorizontalScrollContainer>
    </TabListContainer>
  );
});
